import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../hooks/useAuth";
import leaveService, {
  type CreateLeaveRequestData,
} from "../../../services/leave.service";
import { ceToBe } from "../../../utils/dateUtils";
import { DatePicker } from "../../ui/date-picker";

interface LeaveRequestFormProps {
  onSuccess?: (targetUserId?: string) => void;
  onCancel?: () => void;
}

export const LeaveRequestForm: React.FC<LeaveRequestFormProps> = ({
  onSuccess,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  // Check if user is admin or HR
  const isAdminOrHR =
    currentUser?.role === "admin" || currentUser?.role === "hr";

  // Form state (stored in CE for API compatibility)
  const [formData, setFormData] = useState<
    CreateLeaveRequestData & { targetUserId?: string }
  >({
    leaveTypeId: "",
    startDate: "",
    endDate: "",
    isHalfDayStart: false,
    isHalfDayEnd: false,
    reason: "",
    contactDuringLeave: "",
    emergencyContact: "",
    targetUserId: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch leave types
  const {
    data: leaveTypesData,
    isLoading: isLoadingTypes,
    error: typesError,
  } = useQuery({
    queryKey: ["leaveTypes"],
    queryFn: () => leaveService.getLeaveTypes(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch users for admin/HR
  const {
    data: usersData,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useQuery({
    queryKey: ["users"],
    queryFn: () => leaveService.getUsers(),
    enabled: isAdminOrHR, // Only fetch if user is admin or HR
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Find selected leave type
  const selectedLeaveType = leaveTypesData?.leaveTypes.find(
    (type) => type.id === formData.leaveTypeId
  );

  // Create leave request mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateLeaveRequestData) =>
      leaveService.createLeaveRequest(data),
    onSuccess: (response) => {
      console.log("✅ Leave request created:", response);
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["leaveBalances"] });

      // Reset form
      setFormData({
        leaveTypeId: "",
        startDate: "",
        endDate: "",
        isHalfDayStart: false,
        isHalfDayEnd: false,
        reason: "",
        contactDuringLeave: "",
        emergencyContact: "",
        targetUserId: "",
      });
      setErrors({});

      // Call success callback with targetUserId
      if (onSuccess) {
        onSuccess(formData.targetUserId || undefined);
      }
    },
    onError: (error: any) => {
      console.error("❌ Failed to create leave request:", error);
      // Set form-level error
      setErrors({
        submit:
          error.response?.data?.error?.message ||
          error.message ||
          "Failed to create leave request",
      });
    },
  });

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.leaveTypeId) {
      newErrors.leaveTypeId = "กรุณาเลือกประเภทการลา";
    }

    if (!formData.startDate) {
      newErrors.startDate = "กรุณาเลือกวันที่เริ่มต้น";
    }

    if (!formData.endDate) {
      newErrors.endDate = "กรุณาเลือกวันที่สิ้นสุด";
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end < start) {
        newErrors.endDate = "วันที่สิ้นสุดต้องมาหลังวันที่เริ่มต้น";
      }
    }

    if (!formData.reason || formData.reason.trim().length === 0) {
      newErrors.reason = "กรุณาระบุเหตุผลในการลา";
    } else if (formData.reason.trim().length < 10) {
      newErrors.reason = "เหตุผลต้องมีอย่างน้อย 10 ตัวอักษร";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    // Clean up optional fields
    const submitData: CreateLeaveRequestData & { targetUserId?: string } = {
      ...formData,
      contactDuringLeave: formData.contactDuringLeave?.trim() || undefined,
      emergencyContact: formData.emergencyContact?.trim() || undefined,
      targetUserId:
        isAdminOrHR && formData.targetUserId?.trim()
          ? formData.targetUserId.trim()
          : undefined,
    };

    createMutation.mutate(submitData);
  };

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Admin/HR Note */}
      {isAdminOrHR && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
          <p className="font-medium">💼 โหมด Admin/HR</p>
          <p className="text-sm">
            คุณสามารถสร้างคำขอลาแทนพนักงานได้ เลือกชื่อพนักงานจากรายการด้านล่าง
            หรือเว้นว่างไว้เพื่อลาด้วยตนเอง
          </p>
        </div>
      )}

      {/* Submit Error */}
      {errors.submit && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="font-medium">เกิดข้อผิดพลาด</p>
          <p className="text-sm">{errors.submit}</p>
        </div>
      )}

      {/* Success Message (shown during mutation) */}
      {createMutation.isSuccess && !errors.submit && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <p className="font-medium">สำเร็จ!</p>
          <p className="text-sm">คำขอลาของคุณถูกส่งเรียบร้อยแล้ว</p>
        </div>
      )}

      {/* Target User Selection (Admin/HR only) */}
      {isAdminOrHR && (
        <div>
          <label
            htmlFor="targetUserId"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            ลาแทนพนักงาน (ไม่บังคับ)
          </label>
          {usersError && (
            <p className="text-sm text-red-600 mb-2">
              ไม่สามารถโหลดรายชื่อพนักงานได้ กรุณารีเฟรชหน้านี้
            </p>
          )}
          <select
            id="targetUserId"
            name="targetUserId"
            value={formData.targetUserId || ""}
            onChange={handleChange}
            disabled={isLoadingUsers || createMutation.isPending}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.targetUserId ? "border-red-500" : "border-gray-300"
            } ${isLoadingUsers ? "bg-gray-100" : ""}`}
          >
            <option value="">
              {isLoadingUsers ? "กำลังโหลด..." : "ลาด้วยตนเอง"}
            </option>
            {usersData?.users
              .filter((user) => user.id !== currentUser?.id) // Exclude current user from list
              .map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email}) - {user.role}
                </option>
              ))}
          </select>
          {errors.targetUserId && (
            <p className="text-sm text-red-600 mt-1">{errors.targetUserId}</p>
          )}
          {formData.targetUserId && (
            <p className="text-sm text-green-600 mt-1">
              🎯 กำลังสร้างคำขอลาแทน:{" "}
              {
                usersData?.users.find((u) => u.id === formData.targetUserId)
                  ?.name
              }
            </p>
          )}
        </div>
      )}

      {/* Leave Type */}
      <div>
        <label
          htmlFor="leaveTypeId"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          ประเภทการลา <span className="text-red-500">*</span>
        </label>
        {typesError && (
          <p className="text-sm text-red-600 mb-2">
            ไม่สามารถโหลดประเภทการลาได้ กรุณารีเฟรชหน้านี้
          </p>
        )}
        <select
          id="leaveTypeId"
          name="leaveTypeId"
          value={formData.leaveTypeId}
          onChange={handleChange}
          disabled={isLoadingTypes || createMutation.isPending}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.leaveTypeId ? "border-red-500" : "border-gray-300"
          } ${isLoadingTypes ? "bg-gray-100" : ""}`}
        >
          <option value="">
            {isLoadingTypes ? "กำลังโหลด..." : "เลือกประเภทการลา"}
          </option>
          {leaveTypesData?.leaveTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.icon} {type.name}
              {type.isPaid ? " (ได้รับค่าจ้าง)" : " (ไม่ได้รับค่าจ้าง)"}
            </option>
          ))}
        </select>
        {errors.leaveTypeId && (
          <p className="text-sm text-red-600 mt-1">{errors.leaveTypeId}</p>
        )}
        {selectedLeaveType && selectedLeaveType.description && (
          <p className="text-sm text-gray-600 mt-1">
            {selectedLeaveType.description}
          </p>
        )}
      </div>

      {/* Date Range */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Start Date */}
        <div>
          <label
            htmlFor="startDate"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            วันที่เริ่มต้น <span className="text-red-500">*</span>
            {isAdminOrHR && (
              <span className="ml-2 text-xs text-blue-600 font-normal">
                (สามารถเลือกวันที่ย้อนหลังได้)
              </span>
            )}
          </label>
          <DatePicker
            value={formData.startDate}
            onChange={(ceDate) => {
              setFormData((prev) => ({ ...prev, startDate: ceDate || "" }));
              if (ceDate && errors.startDate) {
                setErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors.startDate;
                  return newErrors;
                });
              }
            }}
            minDate={!isAdminOrHR ? new Date() : undefined}
            disabled={createMutation.isPending}
            placeholder="เลือกวันที่เริ่มต้น"
            className={errors.startDate ? "border-red-500" : ""}
          />
          {errors.startDate && (
            <p className="text-sm text-red-600 mt-1">{errors.startDate}</p>
          )}
          {formData.startDate && (
            <p className="text-xs text-gray-500 mt-1">
              พ.ศ. {ceToBe(new Date(formData.startDate).getFullYear())}
            </p>
          )}
        </div>

        {/* End Date */}
        <div>
          <label
            htmlFor="endDate"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            วันที่สิ้นสุด <span className="text-red-500">*</span>
          </label>
          <DatePicker
            value={formData.endDate}
            onChange={(ceDate) => {
              setFormData((prev) => ({ ...prev, endDate: ceDate || "" }));
              if (ceDate && errors.endDate) {
                setErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors.endDate;
                  return newErrors;
                });
              }
            }}
            minDate={
              formData.startDate
                ? new Date(formData.startDate)
                : !isAdminOrHR
                  ? new Date()
                  : undefined
            }
            disabled={createMutation.isPending}
            placeholder="เลือกวันที่สิ้นสุด"
            className={errors.endDate ? "border-red-500" : ""}
          />
          {errors.endDate && (
            <p className="text-sm text-red-600 mt-1">{errors.endDate}</p>
          )}
          {formData.endDate && (
            <p className="text-xs text-gray-500 mt-1">
              พ.ศ. {ceToBe(new Date(formData.endDate).getFullYear())}
            </p>
          )}
        </div>
      </div>

      {/* Backdated Leave Warning */}
      {isAdminOrHR &&
        formData.startDate &&
        new Date(formData.startDate) <
          new Date(new Date().setHours(0, 0, 0, 0)) && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
            <p className="text-sm flex items-center">
              <span className="text-lg mr-2">⚠️</span>
              <span>
                <strong>คำเตือน:</strong> คุณกำลังสร้างคำขอลาย้อนหลัง
                (วันที่เริ่มต้นอยู่ในอดีต) สิทธิ์นี้มีเฉพาะ Admin และ HR
                เท่านั้น
              </span>
            </p>
          </div>
        )}

      {/* Half Day Options */}
      {selectedLeaveType?.allowHalfDay && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            ตัวเลือกลาครึ่งวัน
          </p>
          <div className="flex gap-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isHalfDayStart"
                checked={formData.isHalfDayStart}
                onChange={handleChange}
                disabled={createMutation.isPending}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                ลาครึ่งวันในวันที่เริ่มต้น
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isHalfDayEnd"
                checked={formData.isHalfDayEnd}
                onChange={handleChange}
                disabled={createMutation.isPending}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                ลาครึ่งวันในวันที่สิ้นสุด
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Reason */}
      <div>
        <label
          htmlFor="reason"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          เหตุผลในการลา <span className="text-red-500">*</span>
        </label>
        <textarea
          id="reason"
          name="reason"
          value={formData.reason}
          onChange={handleChange}
          disabled={createMutation.isPending}
          rows={4}
          placeholder="กรุณาระบุเหตุผลในการลาอย่างละเอียด..."
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.reason ? "border-red-500" : "border-gray-300"
          }`}
        />
        {errors.reason && (
          <p className="text-sm text-red-600 mt-1">{errors.reason}</p>
        )}
        <p className="text-sm text-gray-500 mt-1">
          {formData.reason.length} ตัวอักษร (ขั้นต่ำ 10 ตัวอักษร)
        </p>
      </div>

      {/* Contact During Leave */}
      <div>
        <label
          htmlFor="contactDuringLeave"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          ช่องทางติดต่อระหว่างลา (ไม่บังคับ)
        </label>
        <input
          type="text"
          id="contactDuringLeave"
          name="contactDuringLeave"
          value={formData.contactDuringLeave}
          onChange={handleChange}
          disabled={createMutation.isPending}
          placeholder="หมายเลขโทรศัพท์หรืออีเมลที่สามารถติดต่อได้"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Emergency Contact */}
      <div>
        <label
          htmlFor="emergencyContact"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          ผู้ติดต่อฉุกเฉิน (ไม่บังคับ)
        </label>
        <input
          type="text"
          id="emergencyContact"
          name="emergencyContact"
          value={formData.emergencyContact}
          onChange={handleChange}
          disabled={createMutation.isPending}
          placeholder="ข้อมูลผู้ติดต่อฉุกเฉิน"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Notice Period Warning */}
      {selectedLeaveType?.minDaysNotice && formData.startDate && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
          <p className="text-sm">
            ⚠️ ประเภทการลานี้ต้องแจ้งล่วงหน้าอย่างน้อย{" "}
            <strong>{selectedLeaveType.minDaysNotice} วัน</strong>
          </p>
        </div>
      )}

      {/* Documentation Warning */}
      {selectedLeaveType?.requiresDocumentation && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg">
          <p className="text-sm">
            ℹ️ ประเภทการลานี้ต้องมีเอกสารประกอบ
            {selectedLeaveType.documentationRequiredAfterDays &&
              ` สำหรับการลาที่มากกว่า ${selectedLeaveType.documentationRequiredAfterDays} วัน`}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4 pt-4">
        <button
          type="submit"
          disabled={createMutation.isPending || isLoadingTypes}
          className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {createMutation.isPending ? "กำลังส่ง..." : "ส่งคำขอลา"}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={createMutation.isPending}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
          >
            ยกเลิก
          </button>
        )}
      </div>
    </form>
  );
};

export default LeaveRequestForm;
